import type { ObservationDetail } from "@/entities/observation";
import type { ThemeBreakpoint, ThemeBreakpointAll } from "@/services/theme";
import type { TabsProps } from "antd";
import type { TFunction } from "i18next";

import { Tabs } from "antd";
import { useTranslation } from "react-i18next";

import ObservationSummary from "@/components/Observation/ObservationSummary";
import { TAB_KEYS } from "@/constants/TabKeys";
import { ObservationComments } from "@/modules/observation/observation-comments";
import { ObservationImages } from "@/modules/observation/observation-images";
import { ObservationPosition } from "@/modules/observation/observation-position";
import { useThemeBreakpoints, filterItemsByBreakpoint } from "@/services/theme";

export const getCommentTabItem = (
  observation: ObservationDetail,
  t: TFunction
) => ({
  key: TAB_KEYS.OBSERVATIONS_COMMENTS,
  label: t("comment:plural"),
  className: "observation-comments-tabpane",
  children: <ObservationComments observation={observation} />,
});

type ObservationTabsProps = {
  observation?: ObservationDetail;
};

type ObservationTabItem = Exclude<TabsProps["items"], undefined>[0] & {
  breakpoints: ThemeBreakpoint[] | ThemeBreakpointAll;
};

const ObservationTabs = ({ observation }: ObservationTabsProps) => {
  const { t } = useTranslation();
  const { activeBreakpoint } = useThemeBreakpoints();

  if (!observation?.id) return null;

  const countImages = observation.images?.length || 0;

  const tabs: ObservationTabItem[] = [
    {
      key: TAB_KEYS.OBSERVATIONS_SUMMARY,
      label: t("observation:summary"),
      children: <ObservationSummary observation={observation} />,
      breakpoints: ["xs", "sm", "md", "lg"],
    },
    {
      key: TAB_KEYS.OBSERVATIONS_IMAGES,
      label: t("observation:images", { count: countImages }),
      children: <ObservationImages observation={observation} />,
      breakpoints: "*",
    },
    {
      key: TAB_KEYS.OBSERVATIONS_POSITION,
      label: t("observation:position"),
      children: <ObservationPosition observation={observation} />,
      breakpoints: "*",
    },
    {
      ...getCommentTabItem(observation, t),
      breakpoints: ["xs", "sm", "md", "lg", "xl"],
    },
  ];

  const activeTabs = filterItemsByBreakpoint({
    activeBreakpoint,
    items: tabs,
  });

  return <Tabs items={activeTabs} />;
};

export default ObservationTabs;
